import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import toast, { Toaster } from 'react-hot-toast';
import { notificationsService, notificationPreferencesService } from '../services/supabaseService';
import { useAuth } from './AuthContext';

const NotificationContext = createContext({});

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] = useState(null);
  const [loading, setLoading] = useState(true);

  // Isolated async operations
  const notificationOperations = {
    async loadNotifications() {
      if (!user) return;
      try {
        const { data, error } = await notificationsService?.getAll({ limit: 50 });
        if (!error && data) {
          setNotifications(data);
          const unread = data?.filter(n => n?.status === 'unread')?.length || 0;
          setUnreadCount(unread);
        }
      } catch (error) {
        console.error('Error loading notifications:', error);
      } finally {
        setLoading(false);
      }
    },

    async loadPreferences() {
      if (!user) return;
      try {
        const { data, error } = await notificationPreferencesService?.get(user?.id);
        if (!error && data) {
          setPreferences(data);
        }
      } catch (error) {
        console.error('Error loading preferences:', error);
      }
    },

    clear() {
      setNotifications([]);
      setUnreadCount(0);
      setPreferences(null);
      setLoading(false);
    }
  };

  // Load notifications and preferences on mount
  useEffect(() => {
    if (user) {
      notificationOperations?.loadNotifications();
      notificationOperations?.loadPreferences();
    } else {
      notificationOperations?.clear();
    }
  }, [user]);

  // Real-time subscription
  useEffect(() => {
    if (!user) return;

    const unsubscribe = notificationsService?.subscribe(
      (payload) => {
        if (payload?.eventType === 'INSERT') {
          const newNotification = payload?.new;
          setNotifications(prev => [newNotification, ...prev]);
          setUnreadCount(prev => prev + 1);

          // Show toast if enabled and matches preferences
          if (preferences?.toastEnabled && shouldShowToast(newNotification)) {
            showToast(newNotification);
          }
        } else if (payload?.eventType === 'UPDATE') {
          const updatedNotification = payload?.new;
          setNotifications(prev =>
            prev?.map(n => n?.id === updatedNotification?.id ? updatedNotification : n)
          );
          // Recalculate unread count
          setUnreadCount(prev => {
            const oldNotification = notifications?.find(n => n?.id === updatedNotification?.id);
            if (oldNotification?.status === 'unread' && updatedNotification?.status !== 'unread') {
              return Math.max(0, prev - 1);
            }
            return prev;
          });
        }
      },
      { userId: user?.id }
    );

    return () => unsubscribe?.();
  }, [user, preferences, notifications]);

  // Check if notification should show toast based on preferences
  const shouldShowToast = useCallback((notification) => {
    if (!preferences) return true;

    const typeKey = `${notification?.type}Enabled`;
    const severityKey = `${notification?.type}Severity`;

    const isTypeEnabled = preferences?.[typeKey] !== false;
    const allowedSeverities = preferences?.[severityKey] || [];
    const isSeverityAllowed = allowedSeverities?.includes(notification?.severity);

    return isTypeEnabled && isSeverityAllowed;
  }, [preferences]);

  // Show toast notification
  const showToast = useCallback((notification) => {
    const duration = preferences?.toastDuration || 5000;
    const severity = notification?.severity || 'info';

    const toastOptions = {
      duration,
      position: 'top-right',
      style: getToastStyle(severity),
      icon: getToastIcon(severity),
    };

    toast(notification?.message, toastOptions);
  }, [preferences]);

  // Get toast style based on severity
  const getToastStyle = (severity) => {
    const baseStyle = {
      borderRadius: '10px',
      padding: '16px',
      fontSize: '14px',
      fontWeight: '500',
    };

    const severityStyles = {
      critical: { background: '#dc2626', color: '#fff', border: '2px solid #991b1b' },
      high: { background: '#ea580c', color: '#fff', border: '2px solid #c2410c' },
      medium: { background: '#f59e0b', color: '#fff', border: '2px solid #d97706' },
      low: { background: '#3b82f6', color: '#fff', border: '2px solid #2563eb' },
      info: { background: '#0ea5e9', color: '#fff', border: '2px solid #0284c7' },
    };

    return { ...baseStyle, ...(severityStyles?.[severity] || severityStyles?.info) };
  };

  // Get toast icon based on severity
  const getToastIcon = (severity) => {
    const icons = {
      critical: '🚨',
      high: '⚠️',
      medium: '⚡',
      low: 'ℹ️',
      info: '💡',
    };
    return icons?.[severity] || icons?.info;
  };

  // Mark notification as read
  const markAsRead = async (id) => {
    try {
      const { error } = await notificationsService?.markAsRead(id);
      if (!error) {
        setNotifications(prev =>
          prev?.map(n => n?.id === id ? { ...n, status: 'read', readAt: new Date()?.toISOString() } : n)
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      const { error } = await notificationsService?.markAllAsRead();
      if (!error) {
        setNotifications(prev =>
          prev?.map(n => ({ ...n, status: 'read', readAt: new Date()?.toISOString() }))
        );
        setUnreadCount(0);
      }
    } catch (error) {
      console.error('Error marking all as read:', error);
    }
  };

  // Archive notification
  const archiveNotification = async (id) => {
    try {
      const { error } = await notificationsService?.archive(id);
      if (!error) {
        setNotifications(prev => prev?.filter(n => n?.id !== id));
        const notification = notifications?.find(n => n?.id === id);
        if (notification?.status === 'unread') {
          setUnreadCount(prev => Math.max(0, prev - 1));
        }
      }
    } catch (error) {
      console.error('Error archiving notification:', error);
    }
  };

  // Update preferences
  const updatePreferences = async (newPreferences) => {
    if (!user) return;
    try {
      const { data, error } = await notificationPreferencesService?.update(user?.id, newPreferences);
      if (!error && data) {
        setPreferences(data);
        toast?.success('Preferences updated successfully');
      }
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast?.error('Failed to update preferences');
    }
  };

  // Create notification (for testing/demo)
  const createNotification = async (notificationData) => {
    if (!user) return;
    try {
      const { data, error } = await notificationsService?.create({
        ...notificationData,
        userId: user?.id
      });
      if (!error && data) {
        // Real-time subscription will handle adding to state
        return { success: true, data };
      }
      return { success: false, error };
    } catch (error) {
      console.error('Error creating notification:', error);
      return { success: false, error };
    }
  };

  const value = {
    notifications,
    unreadCount,
    preferences,
    loading,
    markAsRead,
    markAllAsRead,
    archiveNotification,
    updatePreferences,
    createNotification,
    showToast
  };

  return (
    <NotificationContext.Provider value={value}>
      <Toaster
        position="top-right"
        reverseOrder={false}
        gutter={8}
        toastOptions={{
          duration: preferences?.toastDuration || 5000,
          style: {
            maxWidth: '500px',
          },
        }}
      />
      {children}
    </NotificationContext.Provider>
  );
};

export default NotificationProvider;